import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Tag, Sparkles, Mail, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const COUPON_CODE = "MUDANZA10";
const SESSION_KEY = "moveout_popup_dismissed_es";

interface MoveOutCouponPopupProps {
  cityName?: string;
  /** Delay in ms before showing. Default: 25000 (25s) */
  delay?: number;
}

export const MoveOutCouponPopup = ({ cityName = "tu ciudad", delay = 25000 }: MoveOutCouponPopupProps) => {
  const { toast } = useToast();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Show after delay
    const timer = setTimeout(() => setVisible(true), delay);

    // Also show on exit intent (mouse leaving top of viewport)
    const handleMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 5 && !sessionStorage.getItem(SESSION_KEY)) {
        clearTimeout(timer);
        setVisible(true);
      }
    };

    document.addEventListener("mouseleave", handleMouseOut);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", handleMouseOut);
    };
  }, [delay]);

  const dismiss = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
      sessionStorage.setItem(SESSION_KEY, "1");
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast({ title: "Correo inválido", description: "Por favor ingresa un correo válido.", variant: "destructive" });
      return;
    }
    
    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: { "Content-Type": "application/json", "version": "2021-07-28" },
      body: JSON.stringify({
        type: "external_form_submission",
        timestamp: Date.now(),
        formId: "Move Out Coupon Popup",
        formData: { email },
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: "tk_12d30557e33f4e29be39cf7c348f6d99",
        locationId: "M7JmlFFXdtFNLwyfY61D"
      })
    }).catch(() => {});

    setSubmitted(true);
    sessionStorage.setItem(SESSION_KEY, "1");
  };

  const handleCopyCode = async () => {
    await navigator.clipboard.writeText(COUPON_CODE);
    setCodeCopied(true);
    toast({ title: "¡Código copiado!", description: `Usa ${COUPON_CODE} al reservar para obtener 10% de descuento.` });
    setTimeout(() => setCodeCopied(false), 3000);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className={cn(
          "relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl transition-all duration-300",
          closing ? "opacity-0 scale-95 translate-y-4" : "opacity-100 scale-100 translate-y-0"
        )}
      >
        {/* Shimmer top bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-primary via-primary/60 to-primary bg-[length:200%_auto] animate-[shimmer_2s_linear_infinite]" />

        <div className="bg-card p-8">
          {/* Close */}
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>

          {!submitted ? (
            <>
              {/* Icon */}
              <div className="flex justify-center mb-5">
                <div className="relative">
                  <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Tag className="h-10 w-10 text-primary" />
                  </div>
                  <div className="absolute -top-1 -right-1 h-7 w-7 rounded-full bg-green-500 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">%</span>
                  </div>
                </div>
              </div>

              {/* Headline */}
              <h2 className="text-2xl font-extrabold text-center mb-2 leading-tight">
                ¡Espera! Obtén{" "}
                <span className="text-primary">10% de descuento</span>
                <br />en tu limpieza de mudanza
              </h2>
              <p className="text-muted-foreground text-center text-sm mb-6">
                Ingresa tu correo y te enviamos un código exclusivo para tu limpieza en{" "}
                <span className="font-semibold text-foreground">{cityName}</span>.
                Oferta válida por tiempo limitado.
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                    required
                  />
                </div>
                <Button type="submit" size="lg" className="w-full h-12 font-bold bg-primary text-primary-foreground hover:bg-primary/90 btn-pulse">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Recibir Mi Cupón de 10%
                </Button>
              </form>

              <p className="text-center text-xs text-muted-foreground mt-4">
                🔒 Sin spam. Puedes darte de baja en cualquier momento.
              </p>
            </>
          ) : (
            /* Success state */
            <div className="text-center py-4">
              <div className="flex justify-center mb-5">
                <div className="h-20 w-20 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-green-500" />
                </div>
              </div>
              <h2 className="text-2xl font-extrabold mb-2">¡Listo! Revisa tu correo 🎉</h2>
              <p className="text-muted-foreground text-sm mb-6">
                Te enviamos el cupón a <span className="font-semibold text-foreground">{email}</span>.
                Mientras tanto, usa este código directamente:
              </p>

              {/* Coupon code */}
              <button
                onClick={handleCopyCode}
                className={cn(
                  "w-full py-4 rounded-2xl border-2 border-dashed transition-all duration-200 font-mono font-extrabold text-3xl tracking-widest",
                  codeCopied
                    ? "border-green-500 bg-green-50 text-green-600"
                    : "border-primary bg-primary/5 text-primary hover:bg-primary/10"
                )}
              >
                {codeCopied ? "✓ ¡Copiado!" : COUPON_CODE}
              </button>
              <p className="text-xs text-muted-foreground mt-2 mb-6">
                Toca el código para copiarlo · Aplica al reservar en línea
              </p>

              <Button asChild size="lg" className="w-full h-12 font-bold bg-primary text-primary-foreground hover:bg-primary/90">
                <a href="/book">
                  Reservar Ahora con 10% OFF
                </a>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
